import TemplateHeader from '@common-components/message-editor/template-header';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { closeBulkDashboard, setActiveBulkView } from '@common-reducers/CampaignReducer';
import { handleActiveBulkViewNavigationThunk } from '@common-reducers/CampaignThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import BulkMessageEditorWrapper from './message-and-files/bulk-message-editor-wrapper';
import CampaignInternalHeader from './campaign-internal-header';
import TemplateSaveButtons from '@common-components/message-editor/template-save-buttons';
import { Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { isExtensionContext } from '@common/services/detect-context';
import { useRouter } from '@common-reducers/hooks/use-router';
import { CAMPAIGN_PATHS } from '@common/utils/campaign-routes';
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context';

// Bulk views that mean "the user is editing an existing template" rather than
// creating a brand-new one. Used to gate the Save/Delete buttons under the editor.
const EDIT_MODE_VIEWS = new Set([
    'campaignNewTemplateViewFromManagement',
    'campaignNewTemplateViewFromNewCampaignView',
    'campaignNewTemplateViewFromWhatsappView',
]);

export default function NewTemplateView() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const router = useRouter();
    // Non-null under the redesign shell → template editor back/close returns to
    // the Templates tab URL. Null for legacy/extension → existing behaviour.
    const redesignNav = useCampaignsRedesignNav();

    const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);
    const templateId = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.templateId);

    const isExtension = isExtensionContext();
    const isFromCalendarFlow = activeBulkView === 'campaignNewTemplateViewFromWhatsappView';
    const isEditMode = EDIT_MODE_VIEWS.has(activeBulkView);

    // "From WhatsApp" UI (just an X close button, no header) is only used inside
    // the extension. On the website we always show the standard back-header.
    const isFromWhatsapp = isFromCalendarFlow && isExtension;
    const headerTitle = (!isExtension && isFromCalendarFlow)
        ? 'Calendar'
        : t('templates.templateEditor');

    const handleClose = () => {
        // Redesign: URL-drive back to the Templates tab list (reload-safe).
        if (redesignNav) {
            redesignNav.closeTemplate();
            return;
        }
        if (isExtension) {
            dispatch(closeBulkDashboard());
            return;
        }
        // Website fallback: if entered via scheduler flow, return to calendar.
        if (isFromCalendarFlow) {
            dispatch(setActiveBulkView('campaignManagementView'));
            router.navigate('/calendar');
            return;
        }
        dispatch(handleActiveBulkViewNavigationThunk({ navigate: 'fromNewTemplate' }));
        router.navigate(CAMPAIGN_PATHS.manageTemplates);
    };

    const handleBackToTemplates = () => {
        // Redesign: URL-drive back to the Templates tab list (reload-safe).
        if (redesignNav) {
            redesignNav.closeTemplate();
            return;
        }
        // Website + scheduler-origin flow should go back to calendar page.
        if (!isExtension && isFromCalendarFlow) {
            dispatch(setActiveBulkView('campaignManagementView'));
            router.navigate('/calendar');
            return;
        }
        dispatch(handleActiveBulkViewNavigationThunk({ navigate: 'fromNewTemplate' }));
        // Website: pop back to the Manage Templates URL.
        if (!isExtension) router.navigate(CAMPAIGN_PATHS.manageTemplates);
    };

    return (
        <div className="relative flex flex-col w-full h-full">
            {isFromWhatsapp ? (
                <div className="absolute top-2 right-2 z-50">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClose}
                        className="text-primary dark:text-primary"
                    >
                        <X className="h-6 w-6 text-[var(--color-primary)] dark:text-[var(--color-primary)]" />
                    </Button>
                </div>
            ) : (
                <CampaignInternalHeader
                    title={headerTitle}
                    onBackClick={handleBackToTemplates}
                    backTooltip="Back to Manage Templates"
                />
            )}

            <div className="flex flex-col w-full h-full justify-start items-center overflow-y-auto">
                <Stack direction="column" sx={{ width: '800px', justifyContent: 'space-between' }}>
                    <div className="mt-8">
                        <TemplateHeader />

                        <div className="w-full max-w-[800px]">
                            <BulkMessageEditorWrapper isPreview={false} />
                        </div>

                        {/* Save and Delete buttons close to editor */}
                        {isEditMode && (
                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <TemplateSaveButtons
                                    templateId={templateId}
                                    onSaved={redesignNav ? () => redesignNav.closeTemplate() : undefined}
                                />
                            </div>
                        )}
                    </div>
                </Stack>
            </div>
        </div>
    );
}
